import { OcctImportJSResult } from '../types'

/**
 * STEP tessellation, off the main thread.
 *
 * OCCT's ReadStepFile is synchronous and, on a real CAD assembly, runs for many
 * seconds. Called on the main thread it freezes the page for that whole time: the
 * loading splash stops animating mid-draw, and once Chrome's watchdog trips the user
 * gets the "Page Unresponsive" dialog. There is no way to tell the browser to hold off
 * on that dialog — the only cure is to stop blocking, so the parse happens in a worker
 * and the page stays live throughout.
 *
 * The worker is built from a Blob rather than a bundled module because occt-import-js
 * ships as a classic Emscripten script served from /public, loaded via importScripts —
 * which module workers do not support, and whose availability under Vite differs
 * between dev and build. A Blob worker is a classic worker in both.
 *
 * The paths handed to it must be absolute. A blob worker's base URL is the blob URL
 * itself, and a root-relative path cannot be resolved against one — importScripts would
 * throw, and Emscripten would look for the .wasm in the wrong place.
 */

const OCCT_SCRIPT_PATH = '/occt-import-js/occt-import-js.js'
const OCCT_DIR_PATH = '/occt-import-js/'

// Kept free of template literals and modern syntax: this is a classic worker.
const WORKER_SOURCE = `
self.onmessage = function (event) {
  var data = event.data
  try {
    self.importScripts(data.scriptUrl)
  } catch (err) {
    self.postMessage({ error: 'Could not load the CAD engine: ' + err })
    return
  }

  var factory = self.occtimportjs || (typeof occtimportjs !== 'undefined' ? occtimportjs : null)
  if (!factory) {
    self.postMessage({ error: 'The CAD engine did not register itself.' })
    return
  }

  factory({ locateFile: function (path) { return data.wasmDir + path } })
    .then(function (occt) {
      var result
      try {
        result = occt.ReadStepFile(new Uint8Array(data.buffer), data.params)
      } catch (err) {
        self.postMessage({ error: 'Could not read the STEP file: ' + (err && err.message ? err.message : err) })
        return
      }
      if (!result || !result.meshes || result.meshes.length === 0) {
        self.postMessage({ error: 'No meshes found in STEP file' })
        return
      }

      // Copy into real typed arrays and hand the buffers over rather than cloning them:
      // a large assembly is tens of megabytes of vertex data.
      var transfer = []
      var meshes = []
      for (var i = 0; i < result.meshes.length; i++) {
        var m = result.meshes[i]
        var attributes = {}

        var position = new Float32Array(m.attributes.position.array)
        attributes.position = { array: position }
        transfer.push(position.buffer)

        if (m.attributes.normal && m.attributes.normal.array) {
          var normal = new Float32Array(m.attributes.normal.array)
          attributes.normal = { array: normal }
          transfer.push(normal.buffer)
        }
        if (m.attributes.uv && m.attributes.uv.array) {
          var uv = new Float32Array(m.attributes.uv.array)
          attributes.uv = { array: uv }
          transfer.push(uv.buffer)
        }

        var index = new Uint32Array(m.index.array)
        transfer.push(index.buffer)

        meshes.push({ id: m.id, color: m.color || null, attributes: attributes, index: { array: index } })
      }

      self.postMessage({ meshes: meshes }, transfer)
    })
    .catch(function (err) {
      self.postMessage({ error: 'The CAD engine failed to start: ' + err })
    })
}
`

/**
 * Tessellate a STEP file in a worker. Rejects if the worker cannot be created or the
 * parse fails, so the caller can fall back to the main thread.
 *
 * `buffer` is cloned rather than transferred: a failed worker run has to leave the
 * caller something to retry with.
 */
export function readStepFileInWorker(
  buffer: ArrayBuffer,
  params: { createUVs: boolean; linearDeflection?: number; angularDeflection?: number }
): Promise<OcctImportJSResult> {
  return new Promise((resolve, reject) => {
    if (typeof Worker === 'undefined') {
      reject(new Error('Workers are not available'))
      return
    }

    let url: string
    let worker: Worker
    try {
      url = URL.createObjectURL(new Blob([WORKER_SOURCE], { type: 'text/javascript' }))
      worker = new Worker(url)
    } catch (err) {
      reject(err instanceof Error ? err : new Error(String(err)))
      return
    }

    const cleanup = () => {
      worker.terminate()
      URL.revokeObjectURL(url)
    }

    worker.onmessage = (event: MessageEvent) => {
      const data = event.data as { meshes?: OcctImportJSResult['meshes']; error?: string }
      cleanup()
      if (data.error) reject(new Error(data.error))
      else if (data.meshes) resolve({ meshes: data.meshes })
      else reject(new Error('The CAD engine returned nothing.'))
    }

    worker.onerror = (event: ErrorEvent) => {
      cleanup()
      reject(new Error(event.message || 'The CAD worker failed.'))
    }

    worker.postMessage({
      buffer,
      params,
      scriptUrl: new URL(OCCT_SCRIPT_PATH, window.location.href).href,
      wasmDir: new URL(OCCT_DIR_PATH, window.location.href).href,
    })
  })
}
